import { useCallback, useRef, useState } from "react";
import { findScrollRoot } from "~/features/products/pharmacy-picker/pharmacy-picker.utils";

/**
 * Tracks whether a sticky element has become pinned to the top of its scroll
 * container. Render the returned `sentinelRef` element as a sibling immediately
 * BEFORE the sticky element; once the sentinel scrolls above the container's
 * top, `isStuck` flips to true so callers can apply the pinned styling (white
 * background, shadow, etc.). Pass `enabled` to gate attachment (e.g. only while
 * a modal is open).
 *
 * Implemented with a direct scroll/resize measurement (sentinel top vs scroll
 * root top) rather than an IntersectionObserver: it re-measures on every scroll
 * and is robust to the sticky element unmounting/remounting (e.g. toggling
 * between search results and the no-results state) and to fast scrolls.
 * `sentinelRef` is a callback ref so it (re)binds exactly when the node mounts.
 */
export function useStuckObserver(enabled = true): {
	sentinelRef: (node: HTMLDivElement | null) => void;
	isStuck: boolean;
} {
	const [isStuck, setIsStuck] = useState(false);
	const cleanupRef = useRef<(() => void) | null>(null);

	const sentinelRef = useCallback(
		(node: HTMLDivElement | null) => {
			cleanupRef.current?.();
			cleanupRef.current = null;
			if (!enabled || !node) {
				setIsStuck(false);
				return;
			}
			let rafId = 0;
			let attempts = 0;
			const bind = (): boolean => {
				const scrollRoot = findScrollRoot(node);
				if (!scrollRoot) return false;
				const measure = () => {
					// Stuck once the sentinel has scrolled above the scroll container's
					// top (strict, so a sentinel resting exactly at the top — e.g. the
					// search header's — is not considered stuck before any scroll).
					setIsStuck(
						node.getBoundingClientRect().top <
							scrollRoot.getBoundingClientRect().top,
					);
				};
				measure();
				scrollRoot.addEventListener("scroll", measure, { passive: true });
				window.addEventListener("resize", measure);
				cleanupRef.current = () => {
					scrollRoot.removeEventListener("scroll", measure);
					window.removeEventListener("resize", measure);
				};
				return true;
			};
			// Layout may not be settled on the first frame; retry a few times.
			const scheduleBind = () => {
				if (bind() || attempts >= 5) return;
				attempts += 1;
				rafId = requestAnimationFrame(scheduleBind);
				cleanupRef.current = () => {
					if (rafId) cancelAnimationFrame(rafId);
				};
			};
			scheduleBind();
		},
		[enabled],
	);

	return { sentinelRef, isStuck };
}
